import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { gamesRegistry } from '../data/gamesRegistry';
import gamesFeed from '../data/gamesFeed.json';

const PageWrapper: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({ title, description, children }) => (
  <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', color: 'var(--color-ink-primary)', minHeight: '60vh' }}>
    <Helmet>
      <title>{title} | Gamezolve</title>
      {description && <meta name="description" content={description} />}
      <meta property="og:title" content={`${title} | Gamezolve`} />
      {description && <meta property="og:description" content={description} />}
    </Helmet>
    <h1 style={{ fontSize: '2.5rem', marginBottom: '20px', fontFamily: 'var(--font-family-game)' }}>{title}</h1>
    <div style={{ lineHeight: '1.6', fontSize: '1.1rem' }}>
      {children}
    </div>
    <div style={{ marginTop: '40px' }}>
      <Link to="/" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 'bold' }}>&larr; Back to Home</Link>
    </div>
  </div>
);

export const AboutPage = () => (
  <PageWrapper title="About Us" description="Learn about Gamezolve, our mission to build a modern arcade with premium, relaxing HTML5 games for everyone.">
    <p>Welcome to Gamezolve! We are a passion-driven game development studio creating premium, relaxing, and fun HTML5 games for everyone.</p>
    <p>Our goal is to build a modern arcade where you can play the best games directly in your browser without any downloads, sign-ups, or invasive ads. Whether you're looking for a quick puzzle during a break, or a deep strategy game to pass the time, Gamezolve has you covered.</p>
    <p>We believe in high-quality design, smooth performance, and putting the player first.</p>
  </PageWrapper>
);

export const ContactPage = () => (
  <PageWrapper title="Contact Us">
    <p>Have a question, feedback, or a game request? We'd love to hear from you!</p>
    <p>Whether you're reporting a bug or just want to say hi, feel free to reach out. Our support team typically responds within 24-48 hours.</p>
    <p style={{ marginTop: '20px' }}>
      Email us at: <a href="mailto:support@gamezolve.com" style={{ fontWeight: 'bold', color: '#3b82f6', textDecoration: 'none' }}>support@gamezolve.com</a>
    </p>
  </PageWrapper>
);

export const FAQPage = () => (
  <PageWrapper title="Frequently Asked Questions">
    <div style={{ marginBottom: '20px' }}>
      <h3 style={{ marginBottom: '10px' }}>Are the games really free?</h3>
      <p>Yes, all games on our platform are 100% free to play.</p>
    </div>
    <div style={{ marginBottom: '20px' }}>
      <h3 style={{ marginBottom: '10px' }}>Do I need to download anything?</h3>
      <p>No! Our games run directly in your web browser. There's no need to install any apps or software.</p>
    </div>
    <div style={{ marginBottom: '20px' }}>
      <h3 style={{ marginBottom: '10px' }}>Can I play on my phone?</h3>
      <p>Absolutely. All of our games are responsive and optimized for mobile devices, tablets, and desktops.</p>
    </div>
  </PageWrapper>
);

export const PrivacyPage = () => (
  <PageWrapper title="Privacy Policy">
    <p>Last updated: {new Date().toLocaleDateString()}</p>
    <p>Your privacy is critically important to us at Gamezolve. This Privacy Policy explains how we collect, use, and protect your information when you visit our website (gamezolve.com).</p>
    
    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>1. Information We Collect</h3>
    <p>We do not require you to create an account or provide personal information to play our games. Game progress and settings are stored locally on your device using browser local storage or cookies.</p>
    
    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>2. Use of Local Storage</h3>
    <p>We use local storage mechanisms to save your high scores, unlocked levels, and preferences. This data remains on your device and is not transmitted to our servers.</p>
    
    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>3. Analytics</h3>
    <p>We may use basic, anonymized analytics to understand how our website is used (e.g., most popular games, visitor counts) to help us improve the platform. We do not track personally identifiable information.</p>

    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>4. Contact Us</h3>
    <p>If you have any questions about this Privacy Policy, please contact us at support@gamezolve.com.</p>
  </PageWrapper>
);

export const TermsOfServicePage = () => (
  <PageWrapper title="Terms of Service">
    <p>Last updated: {new Date().toLocaleDateString()}</p>
    <p>Welcome to Gamezolve. By accessing or using our website, you agree to be bound by these Terms of Service.</p>
    
    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>1. Use of the Site</h3>
    <p>Gamezolve provides free online games for entertainment purposes. You may use our site for personal, non-commercial use only. You agree not to distribute, modify, or copy our games without explicit permission.</p>
    
    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>2. Intellectual Property</h3>
    <p>All content on Gamezolve, including games, text, graphics, logos, and code, is the property of Gamezolve or its content suppliers and is protected by intellectual property laws.</p>
    
    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>3. Disclaimer of Warranties</h3>
    <p>Our website and games are provided "as is" without any warranties of any kind. We do not guarantee that the site will be error-free or uninterrupted.</p>

    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>4. Limitation of Liability</h3>
    <p>In no event shall Gamezolve be liable for any direct, indirect, incidental, or consequential damages arising out of your use or inability to use the site.</p>

    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>5. Changes to Terms</h3>
    <p>We reserve the right to modify these Terms of Service at any time. Your continued use of the site following any changes signifies your acceptance of those changes.</p>
  </PageWrapper>
);

export const NotFoundPage = () => (
  <PageWrapper title="Page Not Found">
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <h2 style={{ fontSize: '4rem', margin: '0 0 20px', color: '#9ca3af' }}>404</h2>
      <p style={{ fontSize: '1.2rem', marginBottom: '30px' }}>Oops! The page you're looking for doesn't exist.</p>
      <Link to="/" style={{ 
        display: 'inline-block',
        backgroundColor: '#3b82f6', 
        color: 'white', 
        textDecoration: 'none', 
        padding: '12px 24px',
        borderRadius: '8px',
        fontWeight: 'bold',
        transition: 'background 0.2s'
      }}
      >
        Return to Arcade
      </Link>
    </div>
  </PageWrapper>
);

export const CategoriesPage = () => {
  const allGames = [...gamesRegistry, ...(gamesFeed as any[])];
  const categories = Array.from(new Set(allGames.map(g => g.category)));
  
  return (
    <PageWrapper title="Categories" description="Browse all the free HTML5 games available on Gamezolve by category, including Action, Puzzle, Arcade, and Sports.">
      <p>Browse our games by category:</p>
      {categories.map(cat => {
        const gamesInCat = allGames.filter(g => g.category === cat);
        return (
          <div key={cat} style={{ marginTop: '30px' }}>
            <h2 style={{ fontSize: '1.8rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '10px', marginBottom: '20px' }}>{cat}</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gridAutoFlow: 'dense',
              gap: '8px',
              width: '100%'
            }}>
              {gamesInCat.map((game: any) => (
                <Link 
                  key={game.id} 
                  to={game.path || `/game/${game.id}`}
                  style={{ 
                    textDecoration: 'none',
                    aspectRatio: '1 / 1',
                    position: 'relative',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    backgroundColor: '#e5e7eb',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    display: 'block'
                  }}
                  className="game-tile"
                >
                  <img 
                    src={game.thumbnail} 
                    alt={game.title} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease' }} 
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.style.background = 'linear-gradient(135deg, #3b82f6, #8b5cf6)';
                    }}
                  />
                  <div 
                    className="game-tile-overlay"
                    style={{
                      position: 'absolute',
                      bottom: 0, left: 0, right: 0,
                      padding: '40px 15px 15px',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      display: 'flex',
                      alignItems: 'flex-end',
                      transition: 'opacity 0.2s ease',
                      opacity: 0
                    }}
                  >
                    {game.title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </PageWrapper>
  );
};

export const BlogsPage = () => (
  <PageWrapper title="Blogs & News">
    <p>Stay tuned! We will be posting development updates, game tutorials, and news here soon.</p>
  </PageWrapper>
);
